import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3000';

/**
 * Expense Service - API calls for expense management
 */
export const expenseService = {
    /**
     * Create expense
     */
    async createExpense(expenseData) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses`,
                {
                    method: 'POST',
                    headers: authService.getAuthHeaders(),
                    body: JSON.stringify(expenseData)
                }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to create expense');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error creating expense:', error);
            throw error;
        }
    },

    /**
     * Get expenses with filters
     */
    async getExpenses(filters = {}, page = 1, limit = 20) {
        try {
            const params = new URLSearchParams({
                page,
                limit,
                ...filters
            });

            const response = await fetch(
                `${API_BASE_URL}/expenses?${params}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch expenses');
            }

            const data = await response.json();
            return data.success ? { expenses: data.data, pagination: data.pagination } : null;
        } catch (error) {
            console.error('Error fetching expenses:', error);
            throw error;
        }
    },

    /**
     * Get expense by ID
     */
    async getExpenseById(expenseId) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/${expenseId}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch expense');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching expense:', error);
            throw error;
        }
    },

    /**
     * Update expense
     */
    async updateExpense(expenseId, updates) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/${expenseId}`,
                {
                    method: 'PUT',
                    headers: authService.getAuthHeaders(),
                    body: JSON.stringify(updates)
                }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to update expense');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error updating expense:', error);
            throw error;
        }
    },

    /**
     * Delete expense
     */
    async deleteExpense(expenseId) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/${expenseId}`,
                {
                    method: 'DELETE',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to delete expense');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error deleting expense:', error);
            throw error;
        }
    },

    /**
     * Approve expense
     */
    async approveExpense(expenseId, notes = null) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/${expenseId}/approve`,
                {
                    method: 'POST',
                    headers: authService.getAuthHeaders(),
                    body: JSON.stringify({ notes })
                }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to approve expense');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
        }
    },

    /**
     * Mark expense as paid
     */
    async payExpense(expenseId, paymentDetails) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/${expenseId}/pay`,
                {
                    method: 'POST',
                    headers: authService.getAuthHeaders(),
                    body: JSON.stringify(paymentDetails)
                }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to pay expense');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error paying expense:', error);
            throw error;
        }
    },

    /**
     * Reject expense
     */
    async rejectExpense(expenseId, reason) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/${expenseId}/reject`,
                {
                    method: 'POST',
                    headers: authService.getAuthHeaders(),
                    body: JSON.stringify({ reason })
                }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to reject expense');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error rejecting expense:', error);
            throw error;
        }
    },

    /**
     * Link expense to order
     */
    async linkExpenseToOrder(expenseId, orderId) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/${expenseId}/link-order`,
                {
                    method: 'POST',
                    headers: authService.getAuthHeaders(),
                    body: JSON.stringify({ order_id: orderId })
                }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to link expense to order');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error linking expense to order:', error);
            throw error;
        }
    },

    /**
     * Get pending approval expenses
     */
    async getPendingApprovalExpenses() {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/pending-approval`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch pending approval expenses');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching pending approval expenses:', error);
            throw error;
        }
    },

    /**
     * Get expenses summary
     */
    async getExpensesSummary(startDate, endDate, groupBy = 'category') {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/summary?start_date=${startDate}&end_date=${endDate}&group_by=${groupBy}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch expenses summary');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching expenses summary:', error);
            throw error;
        }
    },

    /**
     * Get expenses by order
     */
    async getExpensesByOrder(orderId) {
        try {
            const response = await fetch(
                `${API_BASE_URL}/expenses/by-order/${orderId}`,
                {
                    method: 'GET',
                    headers: authService.getAuthHeaders()
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch expenses by order');
            }

            const data = await response.json();
            return data.success ? data.data : null;
        } catch (error) {
            console.error('Error fetching expenses by order:', error);
            throw error;
        }
    }
};
